import {
  ISeriesPrimitive,
  SeriesAttachedParameter,
  Time,
  IPrimitivePaneRenderer,
  IPrimitivePaneView,
  PrimitivePaneViewZOrder,
} from 'lightweight-charts';

// Data for each trailing candle
export interface TrailingCandleData {
  time: number;         // timestamp in ms (matches main candle)
  mainOpen: number;     // main candle OHLC for reference
  mainHigh: number;
  mainLow: number;
  mainClose: number;
  trailingOpen: number;
  trailingHigh: number;
  trailingLow: number;
  trailingClose: number;
  isValid: boolean;     // from TrailingOHLC.isValid
  isFormingBar: boolean; // true if this is the current forming candle
}

// Renderer data with screen coordinates
interface TrailingCandleRendererData {
  x: number;
  mainOpenY: number;
  mainHighY: number;
  mainLowY: number;
  mainCloseY: number;
  trailingOpenY: number;
  trailingHighY: number;
  trailingLowY: number;
  trailingCloseY: number;
  barWidth: number;
  isBullish: boolean;  // trailing close >= trailing open
  isFormingBar: boolean;
}

// Color configuration for trailing candles
export interface TrailingCandleColors {
  bullishFill: string;
  bullishForming: string;
  bearishFill: string;
  bearishForming: string;
  border: string;
}

// Default colors - high contrast cyan/coral for dark theme
const DEFAULT_COLORS: TrailingCandleColors = {
  bullishFill: '#00d4ff',     // bright cyan - stands out against green main candles
  bullishForming: '#5fffff',  // brighter cyan for forming bar
  bearishFill: '#ff6b35',     // bright coral - stands out against red main candles
  bearishForming: '#ff9966',  // brighter coral for forming bar
  border: '#ffffff',          // white border for edge definition
};

class TrailingCandlePaneRenderer implements IPrimitivePaneRenderer {
  private _data: TrailingCandleRendererData[] = [];
  private _enabled: boolean = true;
  private _minCandleWidth: number = 4;
  private _colors: TrailingCandleColors = DEFAULT_COLORS;

  update(data: TrailingCandleRendererData[], enabled: boolean, minCandleWidth: number, colors: TrailingCandleColors): void {
    this._data = data;
    this._enabled = enabled;
    this._minCandleWidth = minCandleWidth;
    this._colors = colors;
  }

  draw(target: any): void {
    if (!this._enabled) return;

    target.useBitmapCoordinateSpace((scope: any) => {
      const ctx = scope.context as CanvasRenderingContext2D;
      const pixelRatio = scope.horizontalPixelRatio as number;

      // Apply transparency to trailing candles
      ctx.save();
      ctx.globalAlpha = 0.7; // 70% opacity for subtle appearance

      for (const candle of this._data) {
        // Skip if candle is too small (zoomed out)
        if (candle.barWidth < this._minCandleWidth) continue;

        // Scale coordinates by pixel ratio
        const x = candle.x * pixelRatio;
        const barWidth = candle.barWidth * pixelRatio;

        // Calculate mini-candle dimensions: 40% width, centered within main candle
        const miniWidth = barWidth * 0.4;
        const miniX = x - miniWidth / 2;

        // Get y coordinates (already in screen space from getRendererData)
        const openY = candle.trailingOpenY * pixelRatio;
        const closeY = candle.trailingCloseY * pixelRatio;
        const highY = candle.trailingHighY * pixelRatio;
        const lowY = candle.trailingLowY * pixelRatio;

        // Determine colors based on direction and forming status
        const fillColor = candle.isBullish
          ? (candle.isFormingBar ? this._colors.bullishForming : this._colors.bullishFill)
          : (candle.isFormingBar ? this._colors.bearishForming : this._colors.bearishFill);
        const borderColor = this._colors.border;

        // Draw wick (thin line from high to low)
        // Offset slightly right from center so it doesn't overlap main candle's wick
        const wickWidth = 1 * pixelRatio;
        const wickOffset = 2 * pixelRatio; // Small offset from center
        ctx.fillStyle = fillColor;
        ctx.fillRect(
          x + wickOffset - wickWidth / 2,
          highY,
          wickWidth,
          lowY - highY
        );

        // Draw body (filled rectangle from open to close)
        const bodyTop = Math.min(openY, closeY);
        const bodyBottom = Math.max(openY, closeY);
        const bodyHeight = Math.max(bodyBottom - bodyTop, 1 * pixelRatio); // Minimum 1px height

        // Fill body
        ctx.fillStyle = fillColor;
        ctx.fillRect(
          miniX,
          bodyTop,
          miniWidth,
          bodyHeight
        );

        // Draw border outline for visibility
        ctx.strokeStyle = borderColor;
        ctx.lineWidth = 1 * pixelRatio;
        ctx.strokeRect(
          miniX,
          bodyTop,
          miniWidth,
          bodyHeight
        );
      }

      ctx.restore(); // Restore canvas state (resets globalAlpha)
    });
  }
}

class TrailingCandlePaneView implements IPrimitivePaneView {
  private _source: TrailingCandlePrimitive;
  private _renderer: TrailingCandlePaneRenderer;

  constructor(source: TrailingCandlePrimitive) {
    this._source = source;
    this._renderer = new TrailingCandlePaneRenderer();
  }

  zOrder(): PrimitivePaneViewZOrder {
    // 'normal' renders with candles, below 'top' markers
    return 'normal';
  }

  renderer(): IPrimitivePaneRenderer {
    const data = this._source.getRendererData();
    this._renderer.update(data, this._source.isEnabled(), this._source.getMinCandleWidth(), this._source.getColors());
    return this._renderer;
  }
}

export class TrailingCandlePrimitive implements ISeriesPrimitive<Time> {
  private _data: TrailingCandleData[] = [];
  private _paneView: TrailingCandlePaneView;
  private _requestUpdate?: () => void;
  private _series?: SeriesAttachedParameter<Time>['series'];
  private _chart?: SeriesAttachedParameter<Time>['chart'];
  private _enabled: boolean = true;
  private _minCandleWidth: number = 4;
  private _colors: TrailingCandleColors = DEFAULT_COLORS;

  constructor() {
    this._paneView = new TrailingCandlePaneView(this);
  }

  attached(param: SeriesAttachedParameter<Time>): void {
    this._series = param.series;
    this._chart = param.chart;
    this._requestUpdate = param.requestUpdate;
  }

  detached(): void {
    this._series = undefined;
    this._chart = undefined;
    this._requestUpdate = undefined;
  }

  paneViews(): IPrimitivePaneView[] {
    return [this._paneView];
  }

  updateAllViews(): void {
    // Called when chart needs to update views
  }

  setData(data: TrailingCandleData[]): void {
    this._data = data;
    this._requestUpdate?.();
  }

  setEnabled(enabled: boolean): void {
    this._enabled = enabled;
    this._requestUpdate?.();
  }

  isEnabled(): boolean {
    return this._enabled;
  }

  setMinCandleWidth(width: number): void {
    this._minCandleWidth = width;
    this._requestUpdate?.();
  }

  getMinCandleWidth(): number {
    return this._minCandleWidth;
  }

  setColors(colors: TrailingCandleColors): void {
    this._colors = colors;
    this._requestUpdate?.();
  }

  getColors(): TrailingCandleColors {
    return this._colors;
  }

  getRendererData(): TrailingCandleRendererData[] {
    if (!this._series || !this._chart || !this._enabled) return [];

    const timeScale = this._chart.timeScale();
    const result: TrailingCandleRendererData[] = [];

    // Estimate bar width from visible range
    const visibleRange = timeScale.getVisibleLogicalRange();
    if (!visibleRange) return [];

    const chartWidth = (this._chart as any).options().width || 800;
    const barsInView = visibleRange.to - visibleRange.from;
    const barWidth = barsInView > 0 ? chartWidth / barsInView : 10;

    for (const candle of this._data) {
      if (!candle.isValid) continue;

      // Convert timestamp to Time format (seconds)
      const timeInSeconds = Math.floor(candle.time / 1000) as Time;

      // Get x coordinate from time
      const x = timeScale.timeToCoordinate(timeInSeconds);
      if (x === null) continue;

      // Get y coordinates from prices using series price scale
      const mainOpenY = this._series.priceToCoordinate(candle.mainOpen);
      const mainHighY = this._series.priceToCoordinate(candle.mainHigh);
      const mainLowY = this._series.priceToCoordinate(candle.mainLow);
      const mainCloseY = this._series.priceToCoordinate(candle.mainClose);
      const trailingOpenY = this._series.priceToCoordinate(candle.trailingOpen);
      const trailingHighY = this._series.priceToCoordinate(candle.trailingHigh);
      const trailingLowY = this._series.priceToCoordinate(candle.trailingLow);
      const trailingCloseY = this._series.priceToCoordinate(candle.trailingClose);

      // Skip if any coordinate is null (price out of view)
      if (
        mainOpenY === null || mainHighY === null || mainLowY === null || mainCloseY === null ||
        trailingOpenY === null || trailingHighY === null || trailingLowY === null || trailingCloseY === null
      ) continue;

      result.push({
        x,
        mainOpenY,
        mainHighY,
        mainLowY,
        mainCloseY,
        trailingOpenY,
        trailingHighY,
        trailingLowY,
        trailingCloseY,
        barWidth,
        isBullish: candle.trailingClose >= candle.trailingOpen,
        isFormingBar: candle.isFormingBar,
      });
    }

    return result;
  }
}
